import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Autocomplete,
  Card,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  RotateLeft as ResetIcon,
  CloudUpload as UploadIcon,
  Add as AddIcon,
  Close as CloseIcon,
  PersonAddAlt as PersonAddAltIcon,
  MedicalServices as MedicalServicesIcon,
} from '@mui/icons-material';
import { adminAPI, locationAPI } from '../../services/api';
import BulkUploadDialog from '../components/BulkUploadDialog';

// Consistent text field styling
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    bgcolor: 'background.paper',
    fontSize: '0.875rem',
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'grey.300',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'primary.main',
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
  },
};

// Consistent button styling
const primaryButtonSx = {
  borderRadius: 1.5,
  textTransform: 'none',
  fontWeight: 500,
  px: 2.5,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
  },
};

const outlinedButtonSx = {
  borderRadius: 1.5,
  textTransform: 'none',
  fontWeight: 500,
  px: 2.5,
  borderColor: 'grey.300',
  color: 'grey.700',
  '&:hover': {
    borderColor: 'grey.400',
    bgcolor: 'grey.50',
  },
};

function Users() {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [talukaFilter, setTalukaFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [districts, setDistricts] = useState([]);
  const [talukaOptions, setTalukaOptions] = useState([]);
  const [editTalukaOptions, setEditTalukaOptions] = useState([]);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addFormData, setAddFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    mobile: '',
    role: 'ASHA_WORKER',
    asha_code: '',
    district: '',
    block: '',
    village: '',
    anm_id: '',
  });
  const [addTalukaOptions, setAddTalukaOptions] = useState([]);
  const [bulkUploadDialogOpen, setBulkUploadDialogOpen] = useState(false);
  const [anmOptions, setAnmOptions] = useState([]);
  const [addMenuAnchorEl, setAddMenuAnchorEl] = useState(null);
  const [addAnmDialogOpen, setAddAnmDialogOpen] = useState(false);
  const [addAnmFormData, setAddAnmFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    mobile: '',
    district: '',
    block: '',
    village: '',
  });
  const [addAnmTalukaOptions, setAddAnmTalukaOptions] = useState([]);

  // ठाणे is the default जिल्हा everywhere a new record's district gets
  // picked — every other district stays fully selectable, this just saves
  // the extra click since Thane is where this ZP operates.
  const getDefaultDistrict = () => districts.find(d => d.name_mr === 'ठाणे')?.name_mr || '';

  // The backend's email field is Optional[EmailStr] — it accepts a missing/
  // null email for a genuinely-optional field, but rejects an empty string
  // outright as "not a valid email address". The email inputs here save ''
  // when left blank, so every create/update must normalize that to null
  // right before it's sent, or a blank "ईमेल (पर्यायी)" field breaks the save.
  const normalizeEmail = (email) => (email && email.trim() ? email.trim() : null);

  useEffect(() => {
    fetchDistricts();
    fetchAnmUsers();
  }, []);

  // Backfill the Add User dialog's district once the districts list finishes
  // loading, in case it opened (or was left at its initial blank state)
  // before the list was ready.
  useEffect(() => {
    if (districts.length > 0 && !addFormData.district) {
      setAddFormData(prev => (prev.district ? prev : { ...prev, district: getDefaultDistrict() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [districts]);

  useEffect(() => {
    if (districtFilter) {
      const selected = districts.find(d => d._id === districtFilter);
      if (selected && selected.talukas) {
        setTalukaOptions(selected.talukas.map(t => ({ label: t.name_mr, value: t._id })));
      } else {
        setTalukaOptions([]);
      }
    } else {
      setTalukaOptions([]);
      setTalukaFilter('');
    }
  }, [districtFilter, districts]);

  useEffect(() => {
    if (editFormData.district) {
      const selected = districts.find(d => d._id === editFormData.district);
      if (selected && selected.talukas) {
        setEditTalukaOptions(selected.talukas.map(t => ({ label: t.name_mr, value: t._id })));
      } else {
        setEditTalukaOptions([]);
      }
    } else {
      setEditTalukaOptions([]);
    }
  }, [editFormData.district, districts]);

  useEffect(() => {
    // addFormData.district holds the district's Marathi NAME (not its _id) —
    // matches how the app resolves district/taluka on a user's profile
    // (districts.find(d => d.name_mr === user.district), see
    // HBNC/IdentificationSection.js) — so a user created here actually
    // auto-fills correctly in the app, Thane-default or otherwise.
    if (addFormData.district) {
      const selected = districts.find(d => d.name_mr === addFormData.district);
      if (selected && selected.talukas) {
        setAddTalukaOptions(selected.talukas.map(t => ({ label: t.name_mr, value: t.name_mr })));
      } else {
        setAddTalukaOptions([]);
      }
    } else {
      setAddTalukaOptions([]);
    }
  }, [addFormData.district, districts]);

  useEffect(() => {
    // Same name-based storage as addFormData.district — see the comment above.
    if (addAnmFormData.district) {
      const selected = districts.find(d => d.name_mr === addAnmFormData.district);
      if (selected && selected.talukas) {
        setAddAnmTalukaOptions(selected.talukas.map(t => ({ label: t.name_mr, value: t.name_mr })));
      } else {
        setAddAnmTalukaOptions([]);
      }
    } else {
      setAddAnmTalukaOptions([]);
    }
  }, [addAnmFormData.district, districts]);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, districtFilter, talukaFilter, activeFilter, roleFilter]);

  const fetchDistricts = async () => {
    try {
      const response = await locationAPI.getDistricts();
      setDistricts(response.data);
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    }
  };

  const fetchAnmUsers = async () => {
    try {
      const response = await adminAPI.getUsers({ role: 'ANM', limit: 100 });
      setAnmOptions(response.data.users.map(u => ({ label: u.full_name, value: u.id })));
    } catch (err) {
      console.error('Failed to fetch ANM users:', err);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (districtFilter) params.district = districtFilter;
      if (talukaFilter) params.taluka = talukaFilter;
      if (activeFilter !== '') params.is_active = activeFilter === 'true';
      if (roleFilter) params.role = roleFilter;

      const response = await adminAPI.getUsers(params);
      setUsers(response.data.users);
      setTotal(response.data.total);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleReset = () => {
    setSearch('');
    setDistrictFilter('');
    setTalukaFilter('');
    setActiveFilter('');
    setRoleFilter('');
    setPage(0);
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditFormData({
      email: user.email,
      full_name: user.full_name,
      mobile: user.mobile || '',
      role: user.role,
      asha_code: user.asha_code || '',
      district: user.district || '',
      block: user.block || '',
      village: user.village || '',
      is_active: user.is_active,
      anm_id: user.anm_id || '',
    });
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
    setEditFormData({});
  };

  const handleSaveUser = async () => {
    try {
      await adminAPI.updateUser(selectedUser.id, { ...editFormData, email: normalizeEmail(editFormData.email) });
      handleCloseEditDialog();
      fetchUsers();
    } catch (err) {
      console.error('Failed to update user:', err);
      alert(err.response?.data?.detail || 'वापरकर्ता अद्यतनित करण्यात अयशस्वी');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (window.confirm(`${userName} यांना निष्क्रिय करायचे आहे का?`)) {
      try {
        await adminAPI.deleteUser(userId);
        fetchUsers();
      } catch (err) {
        console.error('Failed to delete user:', err);
        alert(err.response?.data?.detail || 'वापरकर्ता निष्क्रिय करण्यात अयशस्वी');
      }
    }
  };

  const handleOpenAddDialog = () => {
    setAddFormData({
      email: '',
      full_name: '',
      password: '',
      mobile: '',
      role: 'ASHA_WORKER',
      asha_code: '',
      district: getDefaultDistrict(),
      block: '',
      village: '',
      anm_id: '',
    });
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
    setAddFormData({
      email: '',
      full_name: '',
      password: '',
      mobile: '',
      role: 'ASHA_WORKER',
      asha_code: '',
      district: '',
      block: '',
      village: '',
      anm_id: '',
    });
  };

  const handleAddUser = async () => {
    try {
      await adminAPI.createUser({ ...addFormData, email: normalizeEmail(addFormData.email) });
      handleCloseAddDialog();
      fetchUsers();
    } catch (err) {
      console.error('Failed to add user:', err);
      alert(err.response?.data?.detail || 'वापरकर्ता तयार करण्यात अयशस्वी');
    }
  };

  const handleOpenAddMenu = (event) => {
    setAddMenuAnchorEl(event.currentTarget);
  };

  const handleCloseAddMenu = () => {
    setAddMenuAnchorEl(null);
  };

  const handleOpenAddDialogFromMenu = () => {
    handleCloseAddMenu();
    handleOpenAddDialog();
  };

  const handleOpenAddAnmDialog = () => {
    handleCloseAddMenu();
    setAddAnmFormData({
      email: '',
      full_name: '',
      password: '',
      mobile: '',
      district: getDefaultDistrict(),
      block: '',
      village: '',
    });
    setAddAnmDialogOpen(true);
  };

  const handleCloseAddAnmDialog = () => {
    setAddAnmDialogOpen(false);
    setAddAnmFormData({
      email: '',
      full_name: '',
      password: '',
      mobile: '',
      district: '',
      block: '',
      village: '',
    });
  };

  const handleAddAnmUser = async () => {
    try {
      await adminAPI.createUser({ ...addAnmFormData, email: normalizeEmail(addAnmFormData.email), role: 'ANM' });
      handleCloseAddAnmDialog();
      fetchUsers();
      // Refresh so this new ANM is immediately selectable in the ASHA
      // worker dialogs' "नियुक्त एएनएम" field without a page reload.
      fetchAnmUsers();
    } catch (err) {
      console.error('Failed to add ANM:', err);
      alert(err.response?.data?.detail || 'एएनएम तयार करण्यात अयशस्वी');
    }
  };

  const handleOpenBulkUploadDialog = () => {
    setBulkUploadDialogOpen(true);
  };

  const handleCloseBulkUploadDialog = () => {
    setBulkUploadDialogOpen(false);
  };

  const handleBulkUploadSuccess = () => {
    fetchUsers();
  };

  return (
    <Box sx={{ maxWidth: 1400 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'grey.900',
          }}
        >
          वापरकर्ते व्यवस्थापन
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenBulkUploadDialog}
            sx={outlinedButtonSx}
          >
            बल्क अपलोड
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={handleOpenAddMenu}
            sx={primaryButtonSx}
          >
            नवीन वापरकर्ता
          </Button>
          <Menu
            anchorEl={addMenuAnchorEl}
            open={Boolean(addMenuAnchorEl)}
            onClose={handleCloseAddMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              elevation: 3,
              sx: {
                mt: 1,
                minWidth: 240,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'grey.200',
                boxShadow: '0 12px 28px -8px rgba(0, 0, 0, 0.18)',
                py: 0.5,
              },
            }}
          >
            <MenuItem onClick={handleOpenAddDialogFromMenu} sx={{ py: 1.25, px: 2, borderRadius: 1, mx: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                <PersonAddAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="आशा कार्यकर्ता जोडा"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'grey.900' }}
              />
            </MenuItem>
            <MenuItem onClick={handleOpenAddAnmDialog} sx={{ py: 1.25, px: 2, borderRadius: 1, mx: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 36, color: 'primary.main' }}>
                <MedicalServicesIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary="एएनएम जोडा"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'grey.900' }}
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {/* Filters */}
      <Card
        sx={{
          p: 2.5,
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          boxShadow: 'none',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              label="शोधा"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="नाव, ईमेल किंवा मोबाइल"
              sx={textFieldSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              size="small"
              options={districts.map(d => ({ label: d.name_mr, value: d._id }))}
              getOptionLabel={(option) => option.label}
              value={districts.map(d => ({ label: d.name_mr, value: d._id })).find(d => d.value === districtFilter) || null}
              onChange={(e, newValue) => setDistrictFilter(newValue?.value || '')}
              renderInput={(params) => (
                <TextField {...params} label="जिल्हा" sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Autocomplete
              size="small"
              options={talukaOptions}
              getOptionLabel={(option) => option.label}
              value={talukaOptions.find(t => t.value === talukaFilter) || null}
              onChange={(e, newValue) => setTalukaFilter(newValue?.value || '')}
              disabled={!districtFilter}
              renderInput={(params) => (
                <TextField {...params} label="तालुका" sx={textFieldSx} />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>भूमिका</InputLabel>
              <Select
                value={roleFilter}
                label="भूमिका"
                onChange={(e) => setRoleFilter(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                <MenuItem value="">सर्व</MenuItem>
                <MenuItem value="ASHA_WORKER">आशा कार्यकर्ता</MenuItem>
                <MenuItem value="ANM">एएनएम</MenuItem>
                <MenuItem value="TALUKA_ADMIN">तालुका प्रशासक</MenuItem>
                <MenuItem value="ADMIN">प्रशासक</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '0.875rem' }}>स्थिती</InputLabel>
              <Select
                value={activeFilter}
                label="स्थिती"
                onChange={(e) => setActiveFilter(e.target.value)}
                sx={{
                  borderRadius: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                <MenuItem value="">सर्व</MenuItem>
                <MenuItem value="true">सक्रिय</MenuItem>
                <MenuItem value="false">निष्क्रिय</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Button
              fullWidth
              variant="text"
              startIcon={<ResetIcon sx={{ fontSize: 18 }} />}
              onClick={handleReset}
              sx={{
                color: 'grey.600',
                textTransform: 'none',
                fontWeight: 500,
                '&:hover': {
                  bgcolor: 'grey.100',
                },
              }}
            >
              रीसेट
            </Button>
          </Grid>
        </Grid>
      </Card>

      {/* Table */}
      <Card
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress size={32} sx={{ color: 'grey.400' }} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    {['नाव', 'ईमेल', 'मोबाइल', 'आशा कोड', 'जिल्हा', 'तालुका', 'गाव', 'एएनएम', 'स्थिती', 'क्रिया'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          color: 'grey.600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          py: 1.5,
                          borderBottom: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        '&:hover': { bgcolor: 'grey.50' },
                        '&:last-child td': { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'grey.900' }}>
                          {user.full_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.mobile || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.asha_code || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.district
                            ? districts.find(d => d._id === user.district)?.name_mr || user.district
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.block
                            ? (() => {
                                const district = districts.find(d => d._id === user.district);
                                return district?.talukas?.find(t => t._id === user.block)?.name_mr || user.block;
                              })()
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.village || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: 'grey.600' }}>
                          {user.anm_id
                            ? anmOptions.find(a => a.value === user.anm_id)?.label || '-'
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Chip
                          label={user.is_active ? 'सक्रिय' : 'निष्क्रिय'}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            bgcolor: user.is_active ? 'success.lighter' : 'grey.100',
                            color: user.is_active ? 'success.dark' : 'grey.500',
                            border: 'none',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2 }}>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditUser(user)}
                            sx={{
                              color: 'grey.500',
                              '&:hover': { color: 'primary.main', bgcolor: 'primary.lighter' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteUser(user.id, user.full_name)}
                            disabled={!user.is_active}
                            sx={{
                              color: 'grey.500',
                              '&:hover': { color: 'error.main', bgcolor: 'error.lighter' },
                              '&.Mui-disabled': { color: 'grey.300' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50]}
              labelRowsPerPage="प्रति पृष्ठ पंक्ती:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} पैकी ${count}`}
              sx={{
                borderTop: '1px solid',
                borderColor: 'grey.200',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: '0.8125rem',
                  color: 'grey.600',
                },
              }}
            />
          </>
        )}
      </Card>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
            वापरकर्ता संपादित करा
          </Typography>
          <IconButton
            onClick={handleCloseEditDialog}
            size="small"
            sx={{ color: 'grey.400', '&:hover': { color: 'grey.600', bgcolor: 'grey.100' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="संपूर्ण नाव" value={editFormData.full_name || ''} onChange={(e) => setEditFormData({ ...editFormData, full_name: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="मोबाइल नंबर"
                value={editFormData.mobile || ''}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setEditFormData({ ...editFormData, mobile: value });
                }}
                placeholder="10 अंकी मोबाइल नंबर"
                helperText="10 अंकी मोबाइल नंबर आवश्यक आहे"
                error={editFormData.mobile && editFormData.mobile.length !== 10}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="ईमेल (पर्यायी)" type="email" value={editFormData.email || ''} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="आशा कोड" value={editFormData.asha_code || ''} onChange={(e) => setEditFormData({ ...editFormData, asha_code: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={districts.map(d => ({ label: d.name_mr, value: d._id }))}
                getOptionLabel={(option) => option.label}
                value={districts.map(d => ({ label: d.name_mr, value: d._id })).find(d => d.value === editFormData.district) || null}
                onChange={(e, newValue) => setEditFormData({ ...editFormData, district: newValue?.value || '', block: '' })}
                renderInput={(params) => <TextField {...params} label="जिल्हा" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={editTalukaOptions}
                getOptionLabel={(option) => option.label}
                value={editTalukaOptions.find(t => t.value === editFormData.block) || null}
                onChange={(e, newValue) => setEditFormData({ ...editFormData, block: newValue?.value || '' })}
                disabled={!editFormData.district}
                renderInput={(params) => <TextField {...params} label="तालुका" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="गाव" value={editFormData.village || ''} onChange={(e) => setEditFormData({ ...editFormData, village: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '0.875rem' }}>भूमिका</InputLabel>
                <Select value={editFormData.role || 'ASHA_WORKER'} label="भूमिका" onChange={(e) => setEditFormData({ ...editFormData, role: e.target.value })} sx={{ borderRadius: 1.5 }}>
                  <MenuItem value="ASHA_WORKER">आशा कार्यकर्ता</MenuItem>
                  <MenuItem value="ADMIN">प्रशासक</MenuItem>
                  <MenuItem value="TALUKA_ADMIN">तालुका प्रशासक</MenuItem>
                  <MenuItem value="ANM">एएनएम</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '0.875rem' }}>स्थिती</InputLabel>
                <Select value={editFormData.is_active ?? true} label="स्थिती" onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.value })} sx={{ borderRadius: 1.5 }}>
                  <MenuItem value={true}>सक्रिय</MenuItem>
                  <MenuItem value={false}>निष्क्रिय</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editFormData.role === 'ASHA_WORKER' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={anmOptions}
                  getOptionLabel={(option) => option.label}
                  value={anmOptions.find(a => a.value === editFormData.anm_id) || null}
                  onChange={(e, newValue) => setEditFormData({ ...editFormData, anm_id: newValue?.value || '' })}
                  renderInput={(params) => <TextField {...params} label="नियुक्त एएनएम" helperText="या आशा कार्यकर्तीवर देखरेख करणारी एएनएम निवडा" sx={textFieldSx} />}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 2 }}>
          <Button onClick={handleCloseEditDialog} sx={{ color: 'grey.600', fontWeight: 500, textTransform: 'none', '&:hover': { bgcolor: 'grey.100' } }}>
            रद्द करा
          </Button>
          <Button onClick={handleSaveUser} variant="contained" sx={primaryButtonSx}>
            जतन करा
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
            नवीन वापरकर्ता जोडा
          </Typography>
          <IconButton
            onClick={handleCloseAddDialog}
            size="small"
            sx={{ color: 'grey.400', '&:hover': { color: 'grey.600', bgcolor: 'grey.100' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="संपूर्ण नाव" value={addFormData.full_name} onChange={(e) => setAddFormData({ ...addFormData, full_name: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="मोबाइल नंबर"
                value={addFormData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setAddFormData({ ...addFormData, mobile: value });
                }}
                placeholder="10 अंकी मोबाइल नंबर"
                helperText="10 अंकी मोबाइल नंबर आवश्यक आहे"
                error={addFormData.mobile && addFormData.mobile.length !== 10}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="ईमेल (पर्यायी)" type="email" value={addFormData.email} onChange={(e) => setAddFormData({ ...addFormData, email: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="पासवर्ड" type="password" value={addFormData.password} onChange={(e) => setAddFormData({ ...addFormData, password: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="आशा कोड" value={addFormData.asha_code} onChange={(e) => setAddFormData({ ...addFormData, asha_code: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={districts.map(d => ({ label: d.name_mr, value: d.name_mr }))}
                getOptionLabel={(option) => option.label}
                value={districts.map(d => ({ label: d.name_mr, value: d.name_mr })).find(d => d.value === addFormData.district) || null}
                onChange={(e, newValue) => setAddFormData({ ...addFormData, district: newValue?.value || '', block: '' })}
                renderInput={(params) => <TextField {...params} label="जिल्हा" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={addTalukaOptions}
                getOptionLabel={(option) => option.label}
                value={addTalukaOptions.find(t => t.value === addFormData.block) || null}
                onChange={(e, newValue) => setAddFormData({ ...addFormData, block: newValue?.value || '' })}
                disabled={!addFormData.district}
                renderInput={(params) => <TextField {...params} label="तालुका" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="गाव" value={addFormData.village} onChange={(e) => setAddFormData({ ...addFormData, village: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: '0.875rem' }}>भूमिका</InputLabel>
                <Select value={addFormData.role} label="भूमिका" onChange={(e) => setAddFormData({ ...addFormData, role: e.target.value })} sx={{ borderRadius: 1.5 }}>
                  <MenuItem value="ASHA_WORKER">आशा कार्यकर्ता</MenuItem>
                  <MenuItem value="ADMIN">प्रशासक</MenuItem>
                  <MenuItem value="TALUKA_ADMIN">तालुका प्रशासक</MenuItem>
                  <MenuItem value="ANM">एएनएम</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {addFormData.role === 'ASHA_WORKER' && (
              <Grid item xs={12}>
                <Autocomplete
                  options={anmOptions}
                  getOptionLabel={(option) => option.label}
                  value={anmOptions.find(a => a.value === addFormData.anm_id) || null}
                  onChange={(e, newValue) => setAddFormData({ ...addFormData, anm_id: newValue?.value || '' })}
                  renderInput={(params) => <TextField {...params} label="नियुक्त एएनएम" helperText="या आशा कार्यकर्तीवर देखरेख करणारी एएनएम निवडा" sx={textFieldSx} />}
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 2 }}>
          <Button onClick={handleCloseAddDialog} sx={{ color: 'grey.600', fontWeight: 500, textTransform: 'none', '&:hover': { bgcolor: 'grey.100' } }}>
            रद्द करा
          </Button>
          <Button onClick={handleAddUser} variant="contained" sx={primaryButtonSx}>
            जोडा
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add ANM Dialog */}
      <Dialog
        open={addAnmDialogOpen}
        onClose={handleCloseAddAnmDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            py: 2,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: 'grey.900' }}>
            नवीन एएनएम जोडा
          </Typography>
          <IconButton
            onClick={handleCloseAddAnmDialog}
            size="small"
            sx={{ color: 'grey.400', '&:hover': { color: 'grey.600', bgcolor: 'grey.100' } }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField fullWidth required label="संपूर्ण नाव" value={addAnmFormData.full_name} onChange={(e) => setAddAnmFormData({ ...addAnmFormData, full_name: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="मोबाइल नंबर"
                value={addAnmFormData.mobile}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                  setAddAnmFormData({ ...addAnmFormData, mobile: value });
                }}
                placeholder="10 अंकी मोबाइल नंबर"
                helperText="10 अंकी मोबाइल नंबर आवश्यक आहे"
                error={addAnmFormData.mobile && addAnmFormData.mobile.length !== 10}
                sx={textFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="ईमेल (पर्यायी)" type="email" value={addAnmFormData.email} onChange={(e) => setAddAnmFormData({ ...addAnmFormData, email: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth required label="पासवर्ड" type="password" value={addAnmFormData.password} onChange={(e) => setAddAnmFormData({ ...addAnmFormData, password: e.target.value })} sx={textFieldSx} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={districts.map(d => ({ label: d.name_mr, value: d.name_mr }))}
                getOptionLabel={(option) => option.label}
                value={districts.map(d => ({ label: d.name_mr, value: d.name_mr })).find(d => d.value === addAnmFormData.district) || null}
                onChange={(e, newValue) => setAddAnmFormData({ ...addAnmFormData, district: newValue?.value || '', block: '' })}
                renderInput={(params) => <TextField {...params} label="जिल्हा" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={addAnmTalukaOptions}
                getOptionLabel={(option) => option.label}
                value={addAnmTalukaOptions.find(t => t.value === addAnmFormData.block) || null}
                onChange={(e, newValue) => setAddAnmFormData({ ...addAnmFormData, block: newValue?.value || '' })}
                disabled={!addAnmFormData.district}
                renderInput={(params) => <TextField {...params} label="तालुका" sx={textFieldSx} />}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="गाव" value={addAnmFormData.village} onChange={(e) => setAddAnmFormData({ ...addAnmFormData, village: e.target.value })} sx={textFieldSx} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid', borderColor: 'grey.200', px: 3, py: 2 }}>
          <Button onClick={handleCloseAddAnmDialog} sx={{ color: 'grey.600', fontWeight: 500, textTransform: 'none', '&:hover': { bgcolor: 'grey.100' } }}>
            रद्द करा
          </Button>
          <Button onClick={handleAddAnmUser} variant="contained" sx={primaryButtonSx}>
            जोडा
          </Button>
        </DialogActions>
      </Dialog>

      <BulkUploadDialog
        open={bulkUploadDialogOpen}
        onClose={handleCloseBulkUploadDialog}
        onSuccess={handleBulkUploadSuccess}
      />
    </Box>
  );
}

export default Users;
